
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action } from 'vuex-class';
import { DateTime } from 'luxon';
import { resolveLocale } from '@/utils/i18n-helpers';
import Toast from '@/utils/toast';
import IconButton from '../shared/suites/IconButton.vue';
import InfoBanner from '../shared/suites/InfoBanner.vue';
import TableHeaders from '../shared/suites/TableHeaders.vue';
import TextButton from '../shared/suites/TextButton.vue';
import WarningModal from '../shared/suites/WarningModal.vue';

@Component<ManageAnnouncements>({
	components: {
		IconButton,
		InfoBanner,
		TableHeaders,
		TextButton,
		WarningModal
	}
})
export default class ManageAnnouncements extends Vue {
	@Action('deleteAnnouncement', { namespace: 'announcements' }) private deleteAnnouncement!: (id: number) => Promise<void>;
	@Prop({ type: Array, required: true }) private announcements!: Announcement[];
	@Prop({ type: Boolean, required: true }) private loading!: boolean;

	private headers: TableHeader[] = [
		{ text: this.$t('settings.locations.announcements.manage.table_headers.time_since_posted') },
		{ text: this.$t('settings.locations.announcements.manage.table_headers.shown_at') },
		{ text: this.$t('settings.locations.announcements.manage.table_headers.message') },
		{ text: this.$t('settings.locations.announcements.manage.table_headers.delete') }
	];
	private showWarningModal: boolean = false;
	private deletingAnnouncement: boolean = false;
	private announcementToDeleteId: number | null = null;

	/**
	 * Get the time since the announcement was posted and format it
	 *
	 * @param {string} date
	 * @return {string}
	 */
	private getTimeSincePosted(date: string): string {
		const createdDate: DateTime = DateTime.fromISO(date);
		const diff = DateTime.now().diff(createdDate, ['days', 'hours', 'minutes']).toObject();

		let diffString: string = '';
		if (diff.days) {
			diffString += `${this.$tc('shared.durations.days', Math.floor(diff.days))} `;
		}
		if (diff.hours) {
			diffString += `${this.$tc('shared.durations.hours', Math.floor(diff.hours))} `;
		}
		if (diff.minutes && diff.minutes > 1) {
			diffString += `${this.$tc('shared.durations.minutes_abbr', Math.floor(diff.minutes))}`;
		}
		else {
			diffString += this.$t('settings.locations.announcements.manage.just_now');
		}

		return diffString;
	}

	/**
	 * Get the localized message
	 * If the current locale is not available, fallback to English
	 * If English is not available, fallback to the first locale
	 *
	 * @param {{ [key: string]: string }} message
	 * @return {string}
	 */
	private getLocalizeMessage(message: { [key: string]: string }): string {
		const currentLocale = resolveLocale(this.$i18n.locale);
		return message[currentLocale] || message.en || message[Object.keys(message)[0]];
	}

	/**
	 * Open the warning modal to confirm the deletion of an announcement
	 *
	 * @param {number} id
	 * @return {void}
	 */
	private openWarningModal(id: number): void {
		this.announcementToDeleteId = id;
		this.showWarningModal = true;
	}

	/**
	 * Close the warning modal
	 *
	 * @return {void}
	 */
	private closeWarningModal(): void {
		this.announcementToDeleteId = null;
		this.showWarningModal = false;
	}

	/**
	 * Delete an announcement
	 *
	 * @param {number} id
	 * @return {Promise<void>}
	 */
	private async handleDelete(id: number): Promise<void> {
		try {
			this.deletingAnnouncement = true;
			await this.deleteAnnouncement(id);
		}
		catch (error) {
			Toast.error(this.$t('settings.locations.announcements.manage.error_deleting_announcement'));
		}
		finally {
			this.showWarningModal = false;
			this.deletingAnnouncement = false;
		}
	}
}
